// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import * as Yup from 'yup';
import moment from 'moment';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { FormProvider } from 'src/components/hook-form';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetComposeIncludeConcept } from 'src/careflow/ph-core/fhir-client/resources';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import DateTimePicker from 'src/components/DateTimePicker/DateTimePicker';
import { isTimeIncludedInDate } from 'src/utils/dates';
import LoadableAutocomplete from 'src/components/LoadableAutocomplete';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import useTenantConfigData from 'src/careflow/ph-core/multi-tenant/tenant/hook/useTenantConfigData';
import CancelButton from 'src/careflow/common/CancelButton';
import { WorkflowService } from 'src/careflow/ph-core/workflow/api/WorkflowService';
import { EpisodeOfCareService } from '../service/EpisodeOfCareService';
import useNotiMessage from 'src/hooks/useNotiMessage';
import SelectSearchMember from 'src/careflow/common/SelectSearchMember';
import SearchMember from '../../case/detail/components/SearchMember/SearchMember';
import {
  getExternalResourceType,
  TaskSelectedMemberOption,
  TaskSelectedMemberValue,
} from '../../task/patient/TasksGrid/TaskModal';
import { getReferenceDisplayByResource } from 'src/utils/fhir';

type FormValue = {
  status: ValueSetComposeIncludeConcept;
  start: string;
  end: string;
  careManager: any;
  episodeOfCareType: ValueSetComposeIncludeConcept;
  reasonForStatusChange?: ValueSetComposeIncludeConcept;
  workflowType?: any;
};

type Props = {
  patient: WrappedPatient | null;
  episodeOfCare: any | null;
  open: boolean;
  onClose: VoidFunction;
  refresh: VoidFunction;
};

export function PatientEpisodeOfCareForm({
  patient,
  episodeOfCare,
  open,
  onClose,
  refresh,
}: Props) {
  const { i18n } = useLocales();
  const { showErrorMessage } = useNotiMessage();

  const { componentsData } = useTenantConfigData();
  const { defaultValues: tenantDefaultValues } = componentsData?.episodeOfCare ?? {};
  const { searchMemberOptions } = componentsData?.searchMember ?? {};
  const [workflows, setWorkflows] = useState<any>(null);
  const [episodeOfCareStatusVS, { isLoading: isEpisodeOfCareStatusVSLoading }] = useValueSet(
    'episode-of-care-status',
    {
      map: ValueSetWrapper,
    }
  );

  const [types, { isLoading: isTypesLoading }] = useValueSet('episodeofcare-type', {
    map: ValueSetWrapper,
  });

  const [statusChangeReasonVS] = useValueSet('episodeofcare-status-change-reason', {
    map: ValueSetWrapper,
  });

  const [{ selectedCareManager, isSearchMemberOpen }, setSelectedCareManager] = useState<{
    selectedCareManager: TaskSelectedMemberOption | TaskSelectedMemberValue | null;
    isSearchMemberOpen: boolean;
  }>({ selectedCareManager: null, isSearchMemberOpen: false });

  const EventSchema = Yup.object().shape({
    status: Yup.mixed().required('Status is required'),
    episodeOfCareType: Yup.mixed().required('Type is required'),
    end: Yup.date()
      .nullable()
      .transform((curr, orig) => (moment(orig).isValid() ? curr : null))
      .test('is-greater', 'End date must be greater than start date', function (value) {
        const startDate = moment(this.parent.start);
        const endDate = moment(value);
        if (!startDate.isValid() || !endDate.isValid()) return true;

        return startDate.isSameOrBefore(
          endDate,
          isTimeIncludedInDate(endDate) && isTimeIncludedInDate(startDate) ? undefined : 'date'
        );
      }),
    workflowType: Yup.mixed().required('Workflow Type is required'),
  });
  const defaultValues = useMemo(() => {
    const defaultType = types
      ?.asListAll?.()
      ?.find?.(({ code }) => code === tenantDefaultValues?.type?.code);
    const defaultEpisodeOfCare = episodeOfCare?.type?.[0]?.coding?.[0]
      ? episodeOfCare.type[0].coding[0]
      : defaultType
      ? defaultType
      : tenantDefaultValues?.type
      ? tenantDefaultValues?.type
      : null;
    const defaultCareManager = episodeOfCare?.careManager ? episodeOfCare?.careManager : null;
    const defaultStatus = episodeOfCare?.status
      ? episodeOfCareStatusVS
          ?.asListAll?.()
          ?.filter?.((x) => x?.code === episodeOfCare?.status)?.[0]
      : null;

    // Extract reason from statusHistory extension
    let defaultReason: any = undefined;
    if (episodeOfCare?.statusHistory && statusChangeReasonVS) {
      const extensionUrl =
        'https://terminology.patientholistics.app/fhir/StructureDefinition/episodeofcare-status-history-reason';
      const statusHistoryWithReason = [...episodeOfCare.statusHistory]
        .reverse()
        .find((entry: any) => entry?.extension?.some((ext: any) => ext.url === extensionUrl));

      if (statusHistoryWithReason) {
        const reasonExtension = statusHistoryWithReason.extension.find(
          (ext: any) => ext.url === extensionUrl
        );
        const reasonCoding = reasonExtension?.valueCodeableConcept?.coding?.[0];

        if (reasonCoding) {
          defaultReason = statusChangeReasonVS
            ?.asListAll?.()
            ?.find(
              (option) => option.code === reasonCoding.code && option.system === reasonCoding.system
            );
        }
      }
    }

    return {
      episodeOfCareType: defaultEpisodeOfCare,
      careManager: defaultCareManager,
      status: defaultStatus,
      start: episodeOfCare?.period?.start || '',
      end: episodeOfCare?.period?.end || '',
      reasonForStatusChange: defaultReason,
      workflowType: null,
    } as FormValue;
  }, [episodeOfCare, tenantDefaultValues, types, statusChangeReasonVS, episodeOfCareStatusVS]);

  const methods = useForm<FormValue>({ resolver: yupResolver(EventSchema), defaultValues });

  const { setValue, reset, control, handleSubmit, formState } = methods;

  const getWorkflowsByType = async () => {
    const workflowsByType = await WorkflowService.getWorkflowByType('eoc');
    const onlyActive = workflowsByType?.filter((item) => item.status === 'Active');
    setWorkflows(onlyActive);
  };

  useEffect(() => {
    if (!open) return;

    reset(defaultValues);
    getWorkflowsByType();
  }, [open]);

  useEffect(() => {
    if (workflows?.length === 1) {
      setValue('workflowType', workflows?.[0], {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [workflows]);

  const onSubmit = async (dataForm: FormValue) => {
    try {
      const payload = {
        patientId: patient?.id,
        status: dataForm?.status?.code,
        careManager: dataForm?.careManager?.reference ? dataForm?.careManager : undefined,
        type: [
          {
            coding: [
              {
                display: dataForm?.episodeOfCareType?.display,
                system: dataForm?.episodeOfCareType?.system,
                code: dataForm?.episodeOfCareType?.code,
              },
            ],
            text: dataForm?.episodeOfCareType?.display,
          },
        ],
        ...((dataForm?.start || dataForm?.end) && {
          period: {
            ...(dataForm?.start && { start: dataForm?.start }),
            ...(dataForm?.end && { end: dataForm?.end }),
          },
        }),
        workflowScope: dataForm?.workflowType?.code,
        managingOrganization: patient?.managingOrganization?.reference
          ? patient?.managingOrganization
          : undefined,
      };

      await EpisodeOfCareService.create(payload);
      await refresh();
      onClose();
    } catch (error) {
      showErrorMessage(i18n('errorMessages.error'));
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  return (
    <Dialog open={open} fullWidth={true} maxWidth={isSearchMemberOpen ? 'lg' : 'sm'}>
      <DialogTitle>
        {episodeOfCare
          ? i18n('patients.details.episodeOfCare.titleEditEpisodeOfCare', 'crs')
          : i18n('patients.details.episodeOfCare.titleAddEpisodeOfCare', 'crs')}
      </DialogTitle>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Card sx={{ m: 2 }}>
          <Grid container>
            <Grid item xs={isSearchMemberOpen ? 6 : 12}>
              <Stack spacing={2} sx={{ p: 2 }}>
                <Controller
                  name={'workflowType'}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { onChange, onBlur, value } = field;
                    return (
                      <>
                        <LoadableAutocomplete
                          fullWidth
                          label={'Workflow Type*'}
                          isLoading={isTypesLoading}
                          value={value}
                          onChange={(_, newValue) => {
                            onChange(newValue);
                          }}
                          error={error?.message}
                          onBlur={onBlur}
                          options={workflows}
                          getOptionLabel={({ name }) => name ?? ''}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          filterOptions={(options, state) =>
                            options.filter((option) =>
                              option.name.toLowerCase().includes(state.inputValue.toLowerCase())
                            )
                          }
                        />
                      </>
                    );
                  }}
                />

                <Controller
                  name={'episodeOfCareType'}
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { onChange, onBlur, value } = field;
                    return (
                      <>
                        <LoadableAutocomplete
                          fullWidth
                          label={'Type*'}
                          isLoading={isTypesLoading}
                          value={value}
                          onChange={(_, newValue) => {
                            onChange(newValue);
                          }}
                          error={error?.message}
                          onBlur={onBlur}
                          options={types?.asListAll?.() ?? []}
                          getOptionLabel={({ display }: ValueSetComposeIncludeConcept) =>
                            display ?? ''
                          }
                        />
                      </>
                    );
                  }}
                />

                <Controller
                  name="status"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { onChange, onBlur, value } = field;
                    return (
                      <>
                        <LoadableAutocomplete
                          fullWidth
                          label={'Status*'}
                          isLoading={isEpisodeOfCareStatusVSLoading}
                          value={value}
                          onChange={(_, newValue) => {
                            onChange(newValue);
                          }}
                          error={error?.message}
                          onBlur={onBlur}
                          options={episodeOfCareStatusVS?.asListAll?.() ?? []}
                          getOptionLabel={({ display }: ValueSetComposeIncludeConcept) =>
                            display ?? ''
                          }
                        />
                      </>
                    );
                  }}
                />

                <Controller
                  name="careManager"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { onChange, value } = field;
                    return (
                      <SelectSearchMember
                        patient={patient ?? undefined}
                        isLoading={false}
                        valueSelected={value}
                        labelText={'patients.details.episodeOfCare.careManager'}
                        options={searchMemberOptions?.episodeOfCareCareManager ?? []}
                        selectedOption={'selectedCareManager'}
                        stateKey={'careManager'}
                        updateSelectedMember={setSelectedCareManager}
                        updateState={onChange}
                      />
                    );
                  }}
                />

                <Typography sx={{ ml: 1, mt: 0.7, py: 2 }}>
                  {i18n('patients.details.episodeOfCare.period', 'crs')}
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ width: 1 }}>
                  <Controller
                    name="start"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <>
                        <DateTimePicker
                          {...field}
                          textFieldProps={{ fullWidth: true }}
                          label={i18n('patients.details.episodeOfCare.start', 'crs')}
                          isTimeOptional
                          errorMessage={error?.message}
                        />
                      </>
                    )}
                  />
                  <Controller
                    name="end"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <DateTimePicker
                        {...field}
                        textFieldProps={{ fullWidth: true }}
                        label={i18n('patients.details.episodeOfCare.end', 'crs')}
                        isTimeOptional
                        errorMessage={error?.message}
                      />
                    )}
                  />
                </Stack>
              </Stack>
            </Grid>
            {isSearchMemberOpen && (
              <Grid item xs={6}>
                <SearchMember
                  patient={patient as WrappedPatient}
                  onSelectResource={(resource, includedResources) => {
                    const display = getReferenceDisplayByResource(
                      resource,
                      includedResources ?? []
                    );
                    setSelectedCareManager({
                      selectedCareManager: resource,
                      isSearchMemberOpen: false,
                    });
                    setValue(
                      'careManager',
                      {
                        reference: `${resource?.resourceType}/${resource?.id}`,
                        display: display,
                      },
                      {
                        shouldDirty: true,
                        shouldValidate: true,
                      }
                    );
                  }}
                  onClear={() => {
                    setSelectedCareManager({
                      selectedCareManager: null,
                      isSearchMemberOpen: false,
                    });
                  }}
                  externalResourceType={
                    selectedCareManager ? getExternalResourceType(selectedCareManager) : undefined
                  }
                />
              </Grid>
            )}
          </Grid>
          <Stack spacing={2} alignItems="center">
            <DialogActions>
              <Box sx={{ flexGrow: 1 }} />

              <CancelButton onClick={handleClose}>{i18n('cancel')}</CancelButton>

              <Button disabled={formState.isSubmitting} variant="contained" type="submit">
                {i18n('saveChanges')}
              </Button>
            </DialogActions>
          </Stack>
        </Card>
      </FormProvider>
    </Dialog>
  );
}
